import { HTML5toTouch } from "rdndmb-html5-to-touch"; // Import the touch transition
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend"; // Use DndProvider from multi-backend
import "../App.css";
import { DraggableMessage } from "../components/Draggables";
import {
  BINARY_FONT,
  BINARY_SYSTEM,
  COLOR_HIGHLIGHTED,
  FONT,
  REGEX_VALID_DECIMAL
} from "../defaults";
import { convertToAlternate, increaseProgress } from "../helpers";
import coin_1 from "../images/coin_1.png";
import coin_16 from "../images/coin_16.png";
import coin_2 from "../images/coin_2.png";
import coin_32 from "../images/coin_32.png";
import coin_4 from "../images/coin_4.png";
import coin_8 from "../images/coin_8.png";
import {
  buttonCheck,
  level0BinaryTask1,
  level0BinaryTask2,
  levelCorrect,
  levelIncorrect,
  levelNoInputMulti
} from "../strings";

const LevelNumber = 0;

function generateNumber(currentProgress) {
  const numbers = [7, 35, 61];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 62) + 1; // Generate integer between 1 and 63.
  }
  return numbers[currentProgress];
}

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  fontSize: "5dvh",
  padding: "0",
  borderRadius: "0.25em",
  width: "7.5dvh",
  margin: "0 auto",
  justifyContent: "center",
};

const containerArrayStyle = {
  display: "flex",
  flexDirection: "row",
  flexBasis: "0",
  flexWrap: "wrap",
  rowGap: "5dvh",
  justifyContent: "center",
  position: "absolute",
  top: "25%",
  width: "100%",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  borderRadius: "0.25em",
};

const inputStyle = {
  fontFamily: FONT,
  maxWidth: "6dvh",
  margin: "0 auto",
  paddingLeft: "0.5dvh",
  paddingRight: "0.5dvh",
};

const headerStyle = {
  marginTop: "4dvh",
  padding: "0 3dvh",
  fontFamily: FONT,
  fontSize: "4dvh",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
};

/**
 * Returns HTML for a single container. Depending on isLast, sometimes returns plus-containers or equal-sign-containers and input field.
 * @param {String} number The number which will be displayed as a binary coin and as decimal.
 * @param {boolean} isLast Whether this is the last/rightmost container being rendered, in which case no plus will be added, and instead an equal sign and input field are added.
 * @param {number} zeroOrOne Can be 0 or 1. Whether the current binary coin is present in binary representation (1) or not (0).
 * @param {Boolean} leadingZero Whether to render zeroOrOne with low or full opacity.
 */
function RenderContainers(number, zeroOrOne, isLast, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero) {
  let coinImage = "";
  let inputIndex = 0;
  
  switch (number) {
    case 32:
      coinImage=coin_32;
      break;
    case 16:
      coinImage=coin_16;
      inputIndex = 1;
      break;
    case 8:
      coinImage=coin_8;
      inputIndex = 2;
      break;
    case 4:
      coinImage=coin_4;
      inputIndex = 3;
      break;
    case 2:
      coinImage=coin_2;
      inputIndex = 4;
      break;
    case 1:
      coinImage=coin_1;
      inputIndex = 5;
      break;
    default:
      console.log("Error RenderContainers(): Invalid number passed as argument!")
  }
  let coin = <span style={{width: "9.5dvh", height: "9.5dvh", opacity: (zeroOrOne === 1) ? "1" : "0.5"}}><img src={coinImage} alt="coin" style={{ width: "100%", height: "100%", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",}}/></span>; // Use image instead of coin object.
  return (
    <div style={{display: "flex", flexDirection: "row"}}>
      <div style={containerStyle}>
        {coin}
        <span style={{fontFamily: BINARY_FONT, opacity: leadingZero ? "0.5" : "1", color: leadingZero ? "white" : COLOR_HIGHLIGHTED}}>{zeroOrOne}</span>
        <span style={{fontSize: "8dvh", fontFamily: FONT, fontWeight: "bold"}}>&#11107;</span>
        <input
        className="input-field"
        style={inputStyle}
        type="text"
        inputMode="numeric"
        value={inputArray[inputIndex]}
        maxLength={2}
        disabled={disableInput}
        placeholder=""
        pattern={REGEX_VALID_DECIMAL}
        onChange={(e) => {
          e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
          let newInputArray = [...inputArray];
          newInputArray[inputIndex] = e.target.value;
          setInputArray(newInputArray);}
        }
        />
      </div>
      {isLast ? "" : 
        <div style={containerStyle}>
          <span style={{visibility: "hidden", width: "9.5dvh", height: "9.5dvh"}}>{coin}</span>
          <span style={{fontFamily: BINARY_FONT, visibility: "hidden"}}>{zeroOrOne}</span>
          <span style={{fontSize: "8dvh", fontFamily: FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <span style={{fontSize: "4dvh"}}>+</span>
        </div>}
      {!isLast ? "" : 
        <div style={containerStyle}>
          <span style={{visibility: "hidden", width: "9.5dvh", height: "9.5dvh"}}>{coin}</span>
          <span style={{fontFamily: BINARY_FONT, visibility: "hidden"}}>{zeroOrOne}</span>
          <span style={{fontSize: "8dvh", fontFamily: FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <span style={{fontSize: "4dvh"}}>=</span>
        </div>}
      {!isLast ? "" : 
        <div style={{...containerStyle, width: "15dvh", alignItems: "flex-start"}}>
          <span style={{visibility: "hidden", width: "9.5dvh", height: "9.5dvh"}}>{coin}</span>
          <span style={{fontFamily: BINARY_FONT, visibility: "hidden"}}>{zeroOrOne}</span>
          <span style={{fontSize: "8dvh", fontFamily: FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <input
          className="input-field"
          style={{fontFamily: FONT}}
          type="text"
          inputMode="numeric"
          value={inputResult}
          maxLength={5}
          disabled={disableInput}
          placeholder=""
          pattern={REGEX_VALID_DECIMAL}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
            setInputResult(e.target.value)}
          }
          />
        </div>
      }
    </div>
  );
}

/**
 * Decides what to render and then renders all containers.
 * @param {number} chosenNumber 
 * @param {String} inputResult Final input on the very right (sum).
 * @param {*} setInputResult 
 * @param {*} inputArray Intermediate inputs (summands).
 * @param {*} setInputArray
 * @param {*} setSolutionArray Solutions (as strings) for intermediate inputs (not for sum).
 * @param {boolean} disableInput 
 * @returns 
 */
function DelegateRenders(chosenNumber, inputResult, setInputResult, inputArray, setInputArray, setSolutionArray, disableInput) {
  let tmpNumber = chosenNumber;
  let containerArray = [];
  let newSolutionArray = new Array(6).fill("0"); // Important that this array is filled with strings, because inputArray is also filled with strings. This way comparisons work.
  let zeroOrOne = 0;
  let leadingZero = true; // Set to false as soon as zeroOrOne is 1 for the first time.
  // For each binary coin number, figure out whether it corresponds to a 1 or a 0. Also set solution (0 or 32/16/8/4/2/1). Then render the corresponding container. Repeat these steps for each coin.
  if (tmpNumber >= 32) {zeroOrOne = 1; leadingZero = false; newSolutionArray[0] = "32"; tmpNumber -= 32;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(32, zeroOrOne, false, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));
  if (tmpNumber >= 16) {zeroOrOne = 1; leadingZero = false; newSolutionArray[1] = "16"; tmpNumber -= 16;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(16, zeroOrOne, false, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));
  if (tmpNumber >= 8) {zeroOrOne = 1; leadingZero = false; newSolutionArray[2] = "8"; tmpNumber -= 8;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(8, zeroOrOne, false, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));
  if (tmpNumber >= 4) {zeroOrOne = 1; leadingZero = false; newSolutionArray[3] = "4"; tmpNumber -= 4;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(4, zeroOrOne, false, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));
  if (tmpNumber >= 2) {zeroOrOne = 1; leadingZero = false; newSolutionArray[4] = "2"; tmpNumber -= 2;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(2, zeroOrOne, false, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));
  if (tmpNumber >= 1) {zeroOrOne = 1; leadingZero = false; newSolutionArray[5] = "1"; tmpNumber -= 1;} else {zeroOrOne = 0;}
  containerArray.push(RenderContainers(1, zeroOrOne, true, inputResult, setInputResult, inputArray, setInputArray, disableInput, leadingZero));

  // Necessary to avoid infinite re-renders.
  useEffect(() => {
    setSolutionArray(newSolutionArray);
  }, [chosenNumber]);

  return <div style={containerArrayStyle}>{containerArray}</div>; // Render the array.
}

function Level0Binary({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [chosenNumber, setChosenNumber] = useState(
    generateNumber(levelProgress[LevelNumber])
  );
  const [inputResult, setInputResult] = useState(""); // Final input on the very right (sum).
  const [inputArray, setInputArray] = useState(new Array(6).fill("")); // Intermediate inputs (summands).
  const [solutionArray, setSolutionArray] = useState(new Array(6).fill("")); // Solutions (as strings) for intermediate inputs (not for sum).
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);
  const [allFilled, setAllFilled] = useState(true); // Boolean flag checking if all input fields have been filled. Used in checkAnswers(). Used to make sure wrong inputs are removed only if all input fields have been filled.

  useEffect(() => {
    setChosenNumber(generateNumber(levelProgress[LevelNumber]));
    setInputResult("");
    const emptyInputArray = new Array(6).fill("");
    setInputArray(emptyInputArray);
  }, [levelProgress]);

  const checkAnswers = () => {
    const isCorrect = (inputResult === chosenNumber.toString()) && (solutionArray.every((value, index) => value === inputArray[index]));
    setCorrect(isCorrect);
    if (isCorrect) {
      setMessage(levelCorrect)
    } else if (inputResult === "" || inputArray.some( (value) => value === "")) {
      setAllFilled(false);
      setMessage(levelNoInputMulti);
    } else {
      setMessage(levelIncorrect);
    }
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, BINARY_SYSTEM); // Triggers a useEffect.
    }
    if (allFilled) { // Only if all input fields have been filled should the wrong inputs be removed. Do not want to help if exercise hasn't been properly tried.
      let newInputArray = [...inputArray];
      newInputArray.map( (value,index) => { // Set only the wrong inputs to empty strings.
        if (value !== solutionArray[index]) {
          newInputArray[index] = "";
        }
      })
      setInputArray(newInputArray);
    }
    if (inputResult !== chosenNumber.toString()) {setInputResult("")} // If sum is false, set it to empty string.
    setMessage("");
    setDisableInput(false);
    setCorrect(false);
    setAllFilled(true);
  };

  return (
    <div>
      <div style={headerStyle}>
        {level0BinaryTask1} <span style={{fontFamily: BINARY_FONT, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{convertToAlternate(BINARY_SYSTEM, chosenNumber)}</span> {level0BinaryTask2}
      </div>
      <DndProvider options={HTML5toTouch}>
        <div>
          {DelegateRenders(chosenNumber, inputResult, setInputResult, inputArray, setInputArray, setSolutionArray, disableInput)}
        </div>
        <button className="checkAnswer-button" onClick={checkAnswers} disabled={disableInput}>
            {buttonCheck}
        </button>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
      </DndProvider>
    </div>
  );
}

export default Level0Binary;
